// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useWorkspaceDatasets } from '../../hooks/WorkspaceDatasetsHooks';
import { ACL_PERMISSIONS } from '../../services/config/accessControl';
import {
  dispatchDeleteRunner,
  dispatchSelectDatastore,
  dispatchSelectDefaultDatastore,
  dispatchStartDatastoreRunner,
  dispatchAddWorkspaceFilePath,
  dispatchAddWorkspaceFilePaths,
  dispatchFetchAllWorkspaceFiles,
  dispatchRemoveWorkspaceFilePath,
  dispatchRemoveWorkspaceFilePaths,
  dispatchUploadDatastoreFiles,
  dispatchDeleteDatastoreFiles,
  dispatchSetLoading,
  dispatchResetValidationState,
  dispatchSetValidationState,
  dispatchUpdateValidationState,
  dispatchStopAllRunnerStatusPollings,
} from '../dispatchers/datastore/DatastoreDispatcher';
import { useFindDatasetById } from './DatasetHooks';

export const useIsLoading = () => {
  return useSelector((state) => state.datastore?.loading);
};
export const useSetLoading = () => {
  const dispatch = useDispatch();
  return useCallback((loading) => dispatch(dispatchSetLoading(loading)), [dispatch]);
};
export const useIsNetworkRequestPending = () => {
  return useSelector((state) => state.datastore?.networkRequestPending);
};

export const useValidationState = () => {
  return useSelector((state) => state.datastore?.validationState);
};
export const useResetValidationState = () => {
  const dispatch = useDispatch();
  return useCallback(() => dispatch(dispatchResetValidationState()), [dispatch]);
};
export const useSetValidationState = () => {
  const dispatch = useDispatch();
  return useCallback((validationState) => dispatch(dispatchSetValidationState(validationState)), [dispatch]);
};
export const useUpdateValidationState = () => {
  const dispatch = useDispatch();
  return useCallback(
    (validationStatePatch) => dispatch(dispatchUpdateValidationState(validationStatePatch)),
    [dispatch]
  );
};

export const useSelectedDatasetId = () => {
  return useSelector((state) => state.datastore?.selectedDatasetId);
};

export const useSelectedDataset = () => {
  const datasetId = useSelectedDatasetId();
  const findDataset = useFindDatasetById();
  return useMemo(() => findDataset(datasetId), [datasetId, findDataset]);
};

export const useDatasetStorageRoot = () => {
  const datasetId = useSelectedDatasetId();
  return useMemo(() => {
    return datasetId != null ? `datastore/${datasetId}/` : null;
  }, [datasetId]);
};

export const useDeleteRunner = () => {
  const dispatch = useDispatch();
  return useCallback((runnerId) => dispatch(dispatchDeleteRunner(runnerId)), [dispatch]);
};

export const useSelectDataset = () => {
  const dispatch = useDispatch();
  return useCallback((datasetId) => dispatch(dispatchSelectDatastore(datasetId)), [dispatch]);
};

export const useVisibleDatastores = () => {
  const datasets = useWorkspaceDatasets();
  return useMemo(() => {
    const visibleDatasets = datasets.filter(
      (dataset) =>
        dataset?.security?.currentUserPermissions?.includes(ACL_PERMISSIONS.DATASET.READ) &&
        dataset?.tags?.includes('datastore')
    );
    visibleDatasets.sort((d1, d2) => (d1.name?.toLowerCase() >= d2.name?.toLowerCase() ? 1 : -1));
    return visibleDatasets;
  }, [datasets]);
};

export const useSelectDefaultDataset = () => {
  const dispatch = useDispatch();
  const selectableDatasets = useVisibleDatastores();
  return useCallback(
    () => dispatch(dispatchSelectDefaultDatastore(selectableDatasets)),
    [dispatch, selectableDatasets]
  );
};

export const useRunners = () => {
  return useSelector((state) => state.datastore?.runners);
};
export const useGetRunnerById = () => {
  const runners = useRunners();
  return useCallback((runnerId) => runners.find((runner) => runner.id === runnerId), [runners]);
};

export const useAssociatedRunners = () => {
  const dataset = useSelectedDataset();
  return useMemo(() => dataset?.associatedRunners, [dataset]);
};
export const useGetAssociatedRunnerFromType = () => {
  const associatedRunners = useAssociatedRunners();
  return useCallback((runnerType) => associatedRunners?.[runnerType], [associatedRunners]);
};

export const useAssociatedValidationRunner = () => {
  const getAssociatedRunnerFromType = useGetAssociatedRunnerFromType();
  return useMemo(() => getAssociatedRunnerFromType('validation'), [getAssociatedRunnerFromType]);
};
export const useAssociatedPushRunner = () => {
  const getAssociatedRunnerFromType = useGetAssociatedRunnerFromType();
  return useMemo(() => getAssociatedRunnerFromType('push'), [getAssociatedRunnerFromType]);
};

export const useStartDatastoreRunner = () => {
  const dispatch = useDispatch();
  const associatedRunners = useAssociatedRunners();
  return useCallback(
    (runnerType) => dispatch(dispatchStartDatastoreRunner(associatedRunners[runnerType].id, runnerType)),
    [associatedRunners, dispatch]
  );
};

export const useGetWorkspaceFiles = () => {
  return useSelector((state) => state.datastore?.workspaceFilePaths);
};

export const useAddWorkspaceFilePath = () => {
  const dispatch = useDispatch();
  return useCallback((path) => dispatch(dispatchAddWorkspaceFilePath([path])), [dispatch]);
};
export const useAddWorkspaceFilePaths = () => {
  const dispatch = useDispatch();
  return useCallback((paths) => dispatch(dispatchAddWorkspaceFilePaths(paths)), [dispatch]);
};

export const useFetchAllWorkspaceFiles = () => {
  const dispatch = useDispatch();
  return useCallback(() => dispatch(dispatchFetchAllWorkspaceFiles()), [dispatch]);
};

export const useRemoveWorkspaceFilePath = () => {
  const dispatch = useDispatch();
  return useCallback((path) => dispatch(dispatchRemoveWorkspaceFilePath(path)), [dispatch]);
};
export const useRemoveWorkspaceFilePaths = () => {
  const dispatch = useDispatch();
  return useCallback((paths) => dispatch(dispatchRemoveWorkspaceFilePaths(paths)), [dispatch]);
};

export const useUploadDatastoreFiles = () => {
  const dispatch = useDispatch();
  return useCallback((files) => dispatch(dispatchUploadDatastoreFiles(files)), [dispatch]);
};
export const useDeleteDatastoreFiles = () => {
  const dispatch = useDispatch();
  return useCallback((filePaths) => dispatch(dispatchDeleteDatastoreFiles(filePaths)), [dispatch]);
};

export const useDatastoreFiles = () => {
  const storageRoot = useDatasetStorageRoot();
  const workspaceFiles = useGetWorkspaceFiles();

  return useMemo(() => {
    if (storageRoot == null) return [];
    return workspaceFiles
      .filter((file) => file.startsWith(storageRoot))
      .map((fileDetail) => ({
        storagePath: fileDetail,
        name: fileDetail.replace(storageRoot, ''),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [storageRoot, workspaceFiles]);
};

export const useIsArchiveAvailable = () => {
  const datastoreFiles = useDatastoreFiles();
  return useMemo(() => datastoreFiles.some((file) => file.name === 'datastore.zip'), [datastoreFiles]);
};
export const useIsReportAvailable = () => {
  const datastoreFiles = useDatastoreFiles();
  return useMemo(() => datastoreFiles.some((file) => file.name === 'validation_report.txt'), [datastoreFiles]);
};

export const useDatastoreTables = () => {
  const datastoreFiles = useDatastoreFiles();
  return useMemo(
    () => datastoreFiles.filter((file) => file.name.endsWith('.csv')).sort((a, b) => a.name.localeCompare(b.name)),
    [datastoreFiles]
  );
};

export const useStopAllRunnerStatusPollings = () => {
  const dispatch = useDispatch();
  return useCallback(() => dispatch(dispatchStopAllRunnerStatusPollings()), [dispatch]);
};
